import { normalizeIndianMobile, type ReportStatus } from '@quickreport/domain';
import { Button, Modal } from '@quickreport/ui';
import type { ReportRecord } from '@quickreport/workspace';
import { Copy, Mail, MessageCircle, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, describeError } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';

interface LinkOut {
  url: string;
  expiresAt: string;
  verifyCode: string;
}

const KIND_LABEL: Record<string, string> = {
  otp_sent: 'A code was sent to the patient',
  otp_view: 'Opened with a code',
  view: 'Viewed',
  pdf: 'PDF downloaded',
  link_made: 'Link made by the lab',
  link_sent: 'Link sent to the patient',
  link_open: 'Opened from a link',
  doctor_link_made: 'Patient made a link for their doctor',
  doctor_link_open: "Opened from the doctor's link",
};

/** Hand a signed report to the patient: send it, copy a link, or open WhatsApp. */
export function SendDialog({
  record,
  sensitive,
  onClose,
}: {
  record: ReportRecord;
  sensitive: boolean;
  onClose: () => void;
}) {
  const ws = useWorkspace();
  const p = record.content.patient;
  const phone = p.phone ? normalizeIndianMobile(p.phone) : null;
  const [link, setLink] = useState<LinkOut | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [log, setLog] = useState<{ kind: string; at: string }[]>([]);
  const [confirmSensitive, setConfirmSensitive] = useState(false);
  const ready = Boolean(record.verifyCode);

  const loadLog = useCallback(async () => {
    if (!ready) return;
    try {
      setLog(
        (
          await api<{ items: { kind: string; at: string }[] }>(
            'GET',
            `/v1/reports/${record.id}/access-log`,
          )
        ).items,
      );
    } catch {
      // offline: the history stays as it was
    }
  }, [ready, record.id]);
  useEffect(() => {
    void loadLog();
  }, [loadLog]);

  async function run<T>(fn: () => Promise<T>): Promise<T | null> {
    setBusy(true);
    setNote(null);
    try {
      return await fn();
    } catch (err) {
      setNote({ tone: 'error', text: describeError(err) });
      return null;
    } finally {
      setBusy(false);
    }
  }

  const makeLink = () =>
    run(async () => {
      if (link) return link;
      const l = await api<LinkOut>('POST', `/v1/reports/${record.id}/link`, {
        confirmSensitive,
      });
      setLink(l);
      return l;
    });

  const message = (url: string) =>
    `Hello ${p.name}, your report from ${ws.auth.orgName} is ready. Open it here (link works for 72 hours): ${url}`;

  async function copy() {
    const l = await makeLink();
    if (!l) return;
    try {
      await navigator.clipboard.writeText(message(l.url));
      setNote({ tone: 'ok', text: 'Message copied. Paste it into any chat.' });
    } catch {
      setNote({ tone: 'error', text: 'Could not copy. Select the link and copy it yourself.' });
    }
  }

  async function whatsapp() {
    const l = await makeLink();
    if (!l) return;
    const to = phone ? phone.replace('+', '') : '';
    window.open(
      `https://wa.me/${to}?text=${encodeURIComponent(message(l.url))}`,
      '_blank',
      'noopener',
    );
  }

  async function email() {
    const l = await makeLink();
    if (!l) return;
    const subject = encodeURIComponent(`${ws.auth.orgName}: your report is ready`);
    window.location.href = `mailto:${p.email ?? ''}?subject=${subject}&body=${encodeURIComponent(message(l.url))}`;
  }

  async function sendNow() {
    const r = await run(() =>
      api<{ queued: number; recipients: string[] }>('POST', `/v1/reports/${record.id}/send`, {}),
    );
    if (r) {
      setNote({
        tone: 'ok',
        text: `Sending to ${r.recipients.join(' and ')}. It usually arrives within a minute.`,
      });
      window.setTimeout(() => void loadLog(), 8000);
    }
  }

  const status: ReportStatus = record.status;
  return (
    <Modal title="Send report to patient" label="Send report" onClose={onClose}>
      {!ready ? (
        <>
          <p className="text-[13px] text-slate">
            {status === 'final' || status === 'partial'
              ? 'This report has not reached the server yet, so it has no link or QR. Connect to the internet and it will be added on its own.'
              : 'Sign the report first. Then you can send it here.'}
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Close</Button>
            {status === 'final' || status === 'partial' ? (
              <Button tone="primary" onClick={() => void run(() => ws.engine.syncNow())}>
                Sync now
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="text-[13px] text-slate">
            {p.name}
            {phone ? ` · ${phone}` : ' · no mobile number'}
            {p.email ? ` · ${p.email}` : ''}
          </div>
          {sensitive ? (
            <label className="flex items-start gap-2 rounded-md border border-line bg-canvas p-3 text-[13px]">
              <input
                type="checkbox"
                className="mt-0.5 accent-brand"
                checked={confirmSensitive}
                onChange={(e) => setConfirmSensitive(e.target.checked)}
              />
              <span>
                This is a sensitive report. It is never sent automatically. Tick to make a link only
                to hand it over in person.
              </span>
            </label>
          ) : null}
          <div className="grid gap-2">
            {sensitive ? null : (
              <Button tone="primary" disabled={busy || (!phone && !p.email)} onClick={sendNow}>
                <Send size={14} /> Send automatically
              </Button>
            )}
            <Button disabled={busy || (sensitive && !confirmSensitive)} onClick={whatsapp}>
              <MessageCircle size={14} /> Open in WhatsApp
            </Button>
            <Button disabled={busy || (sensitive && !confirmSensitive)} onClick={email}>
              <Mail size={14} /> Write an email
            </Button>
            <Button disabled={busy || (sensitive && !confirmSensitive)} onClick={copy}>
              <Copy size={14} /> Copy message with link
            </Button>
          </div>
          {link ? (
            <p className="break-all rounded-md bg-canvas p-2 font-mono text-[11px]">{link.url}</p>
          ) : null}
          {note ? (
            <p
              role={note.tone === 'error' ? 'alert' : 'status'}
              className={`text-[13px] ${note.tone === 'ok' ? 'text-ok' : 'text-flag'}`}
            >
              {note.text}
            </p>
          ) : null}
          <p className="text-[12px] text-slate">
            The link opens the report without a code for 72 hours. Anyone who scans the QR on the
            printed report must enter a code sent to the patient.
          </p>
          {log.length ? (
            <div>
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-slate">
                Recent activity
              </h3>
              <ul className="mt-1 max-h-32 overflow-auto text-[12px]">
                {log.slice(0, 12).map((x) => (
                  <li key={`${x.kind}${x.at}`} className="flex justify-between gap-3 py-0.5">
                    <span>{KIND_LABEL[x.kind] ?? x.kind}</span>
                    <span className="text-slate">
                      {new Date(x.at).toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex justify-end">
            <Button onClick={onClose}>Done</Button>
          </div>
        </>
      )}
    </Modal>
  );
}
